import { PERMISSION_DEPENDENCIES } from "./permissionDependencies";
import { PermissionNew } from "../type";

type GroupedPermissions = Record<string, Record<string, PermissionNew[]>>;

/**
 * When enabling a permission, also enable all permissions it requires.
 * When disabling a permission, also disable all permissions that depend on it.
 */
export function applyPermissionDependencies(
  state: GroupedPermissions,
  changedPermissionName: string,
  isNowEnabled: boolean,
): GroupedPermissions {
  const updated = deepCloneState(state);

  if (isNowEnabled) {
    const deps = PERMISSION_DEPENDENCIES[changedPermissionName];
    if (deps?.requires) {
      for (const requiredName of deps.requires) {
        enablePermissionByName(updated, requiredName);
      }
    }
  } else {
    cascadeDisable(updated, changedPermissionName);
  }

  return updated;
}

function cascadeDisable(state: GroupedPermissions, disabledName: string): void {
  for (const [permName, dep] of Object.entries(PERMISSION_DEPENDENCIES)) {
    if (dep.requires.includes(disabledName)) {
      const perm = findPermissionByName(state, permName);
      if (perm?.isEnable) {
        perm.isEnable = false;
        cascadeDisable(state, permName);
      }
    }
  }
}

function enablePermissionByName(state: GroupedPermissions, name: string): void {
  const perm = findPermissionByName(state, name);
  if (perm) perm.isEnable = true;
}

function findPermissionByName(
  state: GroupedPermissions,
  name: string,
): PermissionNew | undefined {
  for (const category of Object.values(state)) {
    for (const perms of Object.values(category)) {
      const perm = perms.find((p) => p.name === name);
      if (perm) return perm;
    }
  }
  return undefined;
}

export function deepCloneState(state: GroupedPermissions): GroupedPermissions {
  const cloned: GroupedPermissions = {};
  for (const [cat, subcats] of Object.entries(state)) {
    cloned[cat] = {};
    for (const [subcat, perms] of Object.entries(subcats)) {
      cloned[cat][subcat] = perms.map((p) => ({ ...p }));
    }
  }
  return cloned;
}
